<template>
  <div
    class="chat-message-video-slot"
    :class="{ 'chat-slot-row--mine': message.senderId === currentUserId }"
  >
    <div
      v-if="message.senderId !== currentUserId && message.avatar"
      class="vac-avatar"
      :style="{ backgroundImage: `url('${message.avatar}')` }"
    />
    <div
      v-else-if="message.senderId !== currentUserId && showAvatarOffset"
      class="vac-avatar-offset"
    />

    <div
      :class="[
        'vac-message-container',
        { 'vac-message-container-offset': showAvatarOffset },
      ]"
    >
      <div :class="messageCardClass">
        <div v-if="showUsername" class="vac-text-username">
          {{ message.username }}
        </div>

        <div
          v-if="!message.deleted && message.replyMessage"
          class="vac-message-reply-container chat-slot-reply"
        >
          <span class="vac-reply-username">{{
            replyUsername || "…"
          }}</span>
          <span class="vac-reply-content">{{
            message.replyMessage.content || "…"
          }}</span>
        </div>

        <div
          v-if="message.content && !message.deleted"
          class="vac-text-content chat-slot-text"
        >
          {{ message.content }}
        </div>

        <div class="vac-message-files-container chat-slot-files">
          <template v-for="(file, idx) in message.files || []">
            <div
              v-if="isCustomVideoFile(file)"
              :key="'vid-' + idx"
              class="chat-slot-video-wrap"
            >
              <ChatVideoPlayer
                class="chat-slot-video-player"
                :hls-url="videoPayload(file).hlsUrl"
                :thumbnail-url="videoPayload(file).thumbnailUrl"
                :status="videoPayload(file).status"
                :resources-url="resourcesUrl"
                :original-key="videoPayload(file).originalKey"
                :renditions="videoPayload(file).renditions || []"
                :upload-progress="videoPayload(file).uploadProgress || 0"
                :local-preview-url="file.localBlobUrl || null"
                width="100%"
              />
            </div>
            <div
              v-else-if="isImageFile(file)"
              :key="'img-' + idx"
              class="vac-message-image-container chat-slot-img-wrap"
            >
              <img
                class="vac-message-image chat-slot-img"
                :src="file.preview || file.url"
                alt=""
                @click="openInNewTab(file.url || file.preview)"
              />
            </div>
            <a
              v-else
              :key="'f-' + idx"
              class="chat-slot-file-link"
              :href="file.url"
              target="_blank"
              rel="noopener"
            >
              {{ file.name || "File" }}
            </a>
          </template>
        </div>

        <div class="vac-text-timestamp chat-slot-footer">
          <span>{{ message.timestamp }}</span>
          <span v-if="showCheckmark" class="vac-icon-check chat-slot-check">
            {{ message.seen ? "✓✓" : message.distributed ? "✓✓" : "✓" }}
          </span>
        </div>

        <div v-if="canDelete" class="chat-slot-delete">
          <a href="#" @click.prevent="onDelete">{{ $t("chat.deleteMessage") }}</a>
        </div>
      </div>
    </div>

    <div
      v-if="message.senderId === currentUserId && message.avatar"
      class="vac-avatar vac-avatar-current"
      :style="{ backgroundImage: `url('${message.avatar}')` }"
    />
    <div
      v-else-if="message.senderId === currentUserId && showCurrentUserOffset"
      class="vac-avatar-current-offset"
    />
  </div>
</template>

<script>
import ChatVideoPlayer from "./ChatVideoPlayer.vue";

export default {
  name: "ChatMessageVideoSlot",
  components: { ChatVideoPlayer },
  props: {
    message: { type: Object, required: true },
    currentUserId: { type: String, required: true },
    resourcesUrl: { type: String, default: "" },
    roomUsers: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    showUsername() {
      if (!this.message.username || this.message.deleted) return false;
      return (
        this.roomUsers.length > 2 &&
        this.message.senderId !== this.currentUserId
      );
    },
    showAvatarOffset() {
      return (
        this.message.senderId !== this.currentUserId &&
        !this.message.avatar &&
        this.roomUsers.length > 2
      );
    },
    showCurrentUserOffset() {
      return (
        this.message.senderId === this.currentUserId &&
        !this.message.avatar &&
        this.roomUsers.length > 2
      );
    },
    messageCardClass() {
      return [
        "vac-message-card",
        "chat-slot-card",
        {
          "vac-message-current": this.message.senderId === this.currentUserId,
          "vac-message-deleted": this.message.deleted,
        },
      ];
    },
    showCheckmark() {
      return (
        this.message.senderId === this.currentUserId &&
        !this.message.deleted &&
        !this.message.failure
      );
    },
    canDelete() {
      return (
        this.message.senderId === this.currentUserId &&
        !this.message.deleted &&
        !this.message.system
      );
    },
    replyUsername() {
      const r = this.message.replyMessage;
      if (!r) return "";
      const u = this.roomUsers.find((x) => x && x._id === r.senderId);
      return u ? u.username : "";
    },
  },
  methods: {
    isCustomVideoFile(file) {
      if (!file) return false;
      if (file.isChatVideo) return true;
      const t = (file.type || "").toLowerCase();
      const n = (file.name || "").toLowerCase();
      return (
        t.includes("video") ||
        [".mp4", ".webm", ".mov", ".mkv"].some((ext) => n.endsWith(ext))
      );
    },
    isImageFile(file) {
      if (!file || this.isCustomVideoFile(file)) return false;
      const t = (file.type || "").toLowerCase();
      return (
        t.includes("image") ||
        t === "jpeg" ||
        t === "jpg" ||
        t === "png" ||
        t === "gif" ||
        t === "webp"
      );
    },
    videoPayload(file) {
      if (file.videoData) return file.videoData;
      return {
        hlsUrl: null,
        thumbnailUrl: file.preview || null,
        status: "READY",
        originalKey: file.originalKey || null,
        renditions: [],
        uploadProgress: 0,
      };
    },
    openInNewTab(url) {
      if (url) window.open(url, "_blank");
    },
    onDelete() {
      this.$emit("delete", {
        message: {
          messageId: this.message.messageId || this.message._id,
        },
      });
    },
  },
};
</script>

<style scoped>
.chat-message-video-slot {
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 100%;
  align-items: flex-end;
  box-sizing: border-box;
}

/* Own messages: align bubble to the right and give it a stable width */
.chat-slot-row--mine {
  justify-content: flex-end;
}

.chat-slot-row--mine .vac-message-container {
  margin-left: auto;
}

.vac-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin: 0 8px 2px 0;
}

.vac-avatar-current {
  margin: 0 0 2px 8px;
}

.vac-avatar-offset,
.vac-avatar-current-offset {
  min-width: 40px;
  width: 40px;
  margin: 0 8px;
}

.vac-message-container {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  /* Prevent the flex row from collapsing the bubble to a thin strip */
  width: min(360px, calc(100% - 52px));
  max-width: min(360px, calc(100% - 52px));
  min-width: min(280px, 100%);
}

.vac-message-container-offset {
  margin-left: 0;
}

.chat-slot-card {
  padding: 6px 9px 3px;
  border-radius: 8px;
  max-width: 100%;
  box-sizing: border-box;
  white-space: pre-line;
  box-shadow: 0 1px 1px -1px rgba(0, 0, 0, 0.1),
    0 1px 1px -1px rgba(0, 0, 0, 0.11),
    0 1px 2px -1px rgba(0, 0, 0, 0.11);
}

/* Match vue-advanced-chat theme variables (same as default text bubbles) */
.chat-slot-card.vac-message-current {
  background-color: var(--chat-message-bg-color-me, #daf8cb) !important;
  color: var(--chat-message-color, #0a0a0a);
  margin-left: 0;
}

.chat-slot-card:not(.vac-message-current) {
  background-color: var(--chat-message-bg-color, #f6f6f6);
  color: var(--chat-message-color, #0a0a0a);
}

.chat-slot-card.vac-message-deleted {
  opacity: 0.65;
  font-style: italic;
}

.vac-text-username {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #666;
}

.chat-slot-reply {
  font-size: 12px;
  padding: 6px 8px;
  margin-bottom: 6px;
  border-left: 3px solid #1890ff;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.vac-reply-username {
  font-weight: 600;
  margin-right: 6px;
}

.chat-slot-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 6px;
}

.chat-slot-files {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-slot-video-wrap {
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.04);
}

/* Inline player: fill slot width; soften default purple placeholder */
.chat-slot-video-wrap ::v-deep .video-player-wrapper {
  width: 100%;
}

.chat-slot-video-wrap ::v-deep .video-thumbnail-container,
.chat-slot-video-wrap ::v-deep .video-container {
  width: 100% !important;
  max-width: 100% !important;
}

.chat-slot-video-wrap ::v-deep .video-thumbnail-placeholder {
  background: rgba(0, 0, 0, 0.08) !important;
}

.chat-slot-video-wrap ::v-deep .video-thumbnail {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}
.chat-slot-video-wrap ::v-deep .video-player {
  width: 100% !important;
  height: auto !important;
  max-height: 360px;
  object-fit: contain;
  background: #000;
}

.chat-slot-video-wrap ::v-deep .chat-video-preview {
  width: 100% !important;
  height: auto !important;
  max-height: 360px;
  object-fit: contain;
  background: #000;
}

.chat-slot-img {
  max-width: min(280px, 100%);
  max-height: 220px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
}

.chat-slot-file-link {
  font-size: 13px;
  word-break: break-all;
}

.chat-slot-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: var(--chat-message-color-timestamp, rgba(0, 0, 0, 0.5));
  justify-content: flex-end;
}

.chat-slot-check {
  font-size: 12px;
  color: var(--chat-icon-color-checkmark-seen, #9ecdff);
}

.chat-slot-delete {
  margin-top: 6px;
  font-size: 12px;
}

.chat-slot-delete a {
  color: #ff4d4f;
}
</style>
